/**
 * Material Taxonomy Grid Component
 * Grid view for Material Taxonomies (Packaging PCF)
 * Reference: Packaging Pieces grid pattern
 */

import { FC, useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { Input, Breadcrumbs, Button } from '@mondra/ui-components'
import { GET_ALL_MATERIAL_TAXONOMIES } from '../../../../query/material-taxonomies'
import { useQuery } from '@apollo/client'
import { IMaterialTaxonomyGridItem } from './types'
import { getMaterialTaxonomiesVars } from './helpers'
import { filterEnums, getTableData } from '../../../../shared/gridFunctions'
import Table from '../../../../components/Table'
import { ExportGraphqlButton } from '../../../../components/ExportGraphqlButton'
import { EXPORT_MATERIAL_TAXONOMIES } from '../../../../query/export-material-taxonomies'
import { EXPORT_MAX_ROW_COUNT } from '../../../../const'

// @ts-ignore - SCSS module import
import './index.scss'

const MaterialTaxonomyGrid: FC = () => {
  const navigate = useNavigate()

  const [tableData, setTableData] = useState<IMaterialTaxonomyGridItem[]>([])
  const [columnDefs, setColumn] = useState([])
  const [tableSearch, setTableSearch] = useState<string | null>(null)
  const [debouncedTableSearch, setDebouncedTableSearch] = useState(tableSearch)
  const [totalCount, setTotalCount] = useState<string>('')
  const [endCursor, setEndCursor] = useState<string | null>(null)
  const [startCursor, setStartCursor] = useState<string | null>(null)
  const [materialTaxonomiesVars, setMaterialTaxonomiesVars] =
    useState<any>(getMaterialTaxonomiesVars)
  const [order, setOrder] = useState([{ id: 'ASC' }])

  const { data, loading, refetch } = useQuery(GET_ALL_MATERIAL_TAXONOMIES, {
    variables: {
      str: '',
      first: 100,
      last: null,
      order: order,
      ...getMaterialTaxonomiesVars,
    },
  })

  const anyRefetch = useCallback(
    (variables?: any): Promise<any> => {
      return refetch(variables)
    },
    [refetch]
  )

  const setCursors = useCallback(() => {
    if (data?.taxonomyPackagingMaterialsFunctionalUnits?.pageInfo) {
      setEndCursor(data.taxonomyPackagingMaterialsFunctionalUnits.pageInfo.endCursor)
      setStartCursor(data.taxonomyPackagingMaterialsFunctionalUnits.pageInfo.startCursor)
    }
  }, [data])

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setTableSearch(debouncedTableSearch), 1000)
    return () => clearTimeout(timer)
  }, [debouncedTableSearch])

  // Update table data when query data changes
  useEffect(() => {
    refetch()
    getTableData(
      data,
      loading,
      setTableData,
      setColumn,
      setTotalCount,
      setCursors,
      '/packaging/material-taxonomies/material-taxonomy',
      'taxonomyPackagingMaterialsFunctionalUnits',
      'taxonomyPackagingMaterialsFunctionalUnit'
    )
  }, [data, loading, refetch, setCursors])

  // Handle search and sorting changes
  useEffect(() => {
    if (tableSearch != null || !order[0].id) {
      if (!loading) {
        setMaterialTaxonomiesVars({
          ...filterEnums(tableSearch, getMaterialTaxonomiesVars, data),
        })
        anyRefetch({
          str: tableSearch || '',
          ...filterEnums(tableSearch, getMaterialTaxonomiesVars, data),
          first: 100,
          after: null,
          before: null,
          last: null,
          order: order,
        })
      }
    }
  }, [tableSearch, order, loading, data, anyRefetch])

  // Export payload configuration
  const exportPayload = useMemo(
    () => ({
      operationName: 'TaxonomyPackagingMaterialsFunctionalUnits',
      query: EXPORT_MATERIAL_TAXONOMIES.loc?.source.body,
      variables: {
        first: EXPORT_MAX_ROW_COUNT,
        after: null,
        where: {
          id: { gt: 0 },
          or: [
            { name: { contains: tableSearch || '' } },
            { code: { contains: tableSearch || '' } },
            { class: { contains: tableSearch || '' } },
            { category: { contains: tableSearch || '' } },
            { lastUpdatedName: { contains: tableSearch || '' } },
            { researcherName: { contains: tableSearch || '' } },
            { state: materialTaxonomiesVars.state },
          ],
        },
        order,
      },
    }),
    [order, tableSearch, materialTaxonomiesVars]
  )

  const handleAddNew = useCallback(() => {
    navigate('/packaging/material-taxonomies/material-taxonomy?itemId=new')
  }, [navigate])

  return (
    <div className={'packaging'}>
      <div style={{ maxWidth: '50rem' }} className={'mb-4'}>
        <p className={'mt-3 decoration-gray-600'}>
          <Breadcrumbs
            routes={[{ path: '/packaging/material-taxonomies', title: 'Material Taxonomies' }]}
          />
        </p>
        <h2 className={'mb-5 mt-3 text-2xl'}>Material Taxonomies</h2>
        <div className="packaging-actions items-center">
          <Input
            onChange={e => setDebouncedTableSearch(e.target.value)}
            iconType="search"
            placeholder="Search"
          />
          <Button onClick={handleAddNew}>Add Material Taxonomy</Button>
          <ExportGraphqlButton payload={exportPayload} />
        </div>
      </div>
      <Table
        loading={loading}
        tableData={tableData}
        columnDefs={columnDefs}
        totalNumber={totalCount}
        endCursor={endCursor}
        startCursor={startCursor}
        refetch={refetch}
        defaultVars={materialTaxonomiesVars || getMaterialTaxonomiesVars}
        setCursors={setCursors}
        tableSearch={tableSearch}
        setOrder={setOrder}
        order={order}
        suppressRowClickSelection
      />
    </div>
  )
}

export default MaterialTaxonomyGrid
